import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DeferredChart } from "@/components/DeferredChart";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { inr } from "@/lib/utils-format";
import {
  RefreshCw, TrendingDown, DollarSign, Package, AlertTriangle, Plus, Trash2, Trash,
  Factory, Percent, BarChart3,
} from "lucide-react";

const varianceTone = (pct) =>
  pct >= 10 ? "bg-red-100 text-red-800" : pct >= 5 ? "bg-amber-100 text-amber-800" : pct > 0 ? "bg-yellow-50 text-yellow-800" : "bg-emerald-100 text-emerald-800";

const yieldTone = (pct) =>
  pct >= 95 ? "bg-emerald-100 text-emerald-800" : pct >= 80 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";

export default function YieldAnalysisPage() {
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branchFilter, setBranchFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(null); // batch detail
  const [wastageOpen, setWastageOpen] = useState(false);
  const [wastageForm, setWastageForm] = useState({});

  const loadRefs = async () => {
    try {
      const [b, p, i, r] = await Promise.all([
        api.get("/branches"), api.get("/products"), api.get("/ingredients"),
        api.get("/mfg/consumption/wastage-reasons"),
      ]);
      setBranches(b.data); setProducts(p.data); setIngredients(i.data); setReasons(r.data);
    } catch { /* */ }
  };
  useEffect(() => { loadRefs(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (branchFilter) params.set("branch_id", branchFilter);
      if (productFilter) params.set("product_id", productFilter);
      const [s, b] = await Promise.all([
        api.get(`/mfg/consumption/yield-summary?${params.toString()}`),
        api.get(`/mfg/consumption/batches?${params.toString()}`),
      ]);
      setSummary(s.data); setBatches(b.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate, branchFilter, productFilter]);

  const openBatch = async (batch_id) => {
    try {
      const r = await api.get(`/mfg/consumption/batch/${batch_id}`);
      setDrawer(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveWastage = async () => {
    if (!wastageForm.ingredient_id || !wastageForm.quantity || !wastageForm.reason) {
      toast.error("Ingredient, quantity and reason required"); return;
    }
    try {
      await api.post(`/mfg/consumption/batch/${drawer.batch.id}/wastage`, {
        ingredient_id: wastageForm.ingredient_id,
        quantity: Number(wastageForm.quantity),
        reason: wastageForm.reason,
        notes: wastageForm.notes,
      });
      toast.success("Wastage logged");
      setWastageOpen(false); setWastageForm({});
      openBatch(drawer.batch.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delWastage = async (wid) => {
    if (!window.confirm("Remove this wastage entry and refund ingredient stock?")) return;
    try {
      await api.delete(`/mfg/consumption/batch/${drawer.batch.id}/wastage/${wid}`);
      toast.success("Removed"); openBatch(drawer.batch.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const t = summary?.totals || {};

  return (
    <div className="space-y-6" data-testid="yield-analysis-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Manufacturing · RM & Yield</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Consumption & Yield Analysis</h1>
          <p className="text-sm text-[#8B7355] mt-1">Actual vs standard raw-material usage, wastage by reason, and per-factory yield trends.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="yield-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end p-3 bg-white border border-[#EAE5D9] rounded-md">
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="yield-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="yield-to" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Factory</Label>
          <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="yield-branch"><SelectValue placeholder="All factories" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All factories</SelectItem>
              {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Product</Label>
          <Select value={productFilter} onValueChange={(v) => setProductFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-60" data-testid="yield-product"><SelectValue placeholder="All products" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72 overflow-y-auto">
              <SelectItem value="__all">All products</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals */}
      <section>
        <h2 className="uc-label text-[#5C4033] mb-3">Totals · {fromDate} → {toDate}</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiTile label="Batches" value={t.batches || 0} icon={Package} tone="neutral" testid="yield-kpi-batches" />
          <KpiTile label="Output Qty" value={Math.round(t.output_qty || 0)} icon={Package} tone="emerald" testid="yield-kpi-output" />
          <KpiTile label="Std RM Cost" value={inr(t.standard_rm_cost || 0)} icon={DollarSign} tone="neutral" isText testid="yield-kpi-std" />
          <KpiTile label="Wastage Cost" value={inr(t.wastage_cost || 0)} icon={TrendingDown} tone={t.wastage_cost > 0 ? "red" : "emerald"} isText testid="yield-kpi-wastage" />
          <KpiTile label="RM Variance" value={`${t.rm_variance_pct || 0}%`} icon={Percent} tone={(t.rm_variance_pct || 0) >= 10 ? "red" : (t.rm_variance_pct || 0) >= 5 ? "amber" : "emerald"} isText testid="yield-kpi-variance" />
        </div>
      </section>

      {/* Per-factory */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-md p-4">
          <div className="uc-label mb-3"><Factory className="w-3 h-3 inline mr-1" /> By Factory</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="yield-by-branch">
              <thead className="text-[#8B7355] text-[11px] uppercase">
                <tr>
                  <th className="text-left py-1">Factory</th>
                  <th className="text-right py-1">Batches</th>
                  <th className="text-right py-1">Output</th>
                  <th className="text-right py-1">Std RM ₹</th>
                  <th className="text-right py-1">Wastage ₹</th>
                  <th className="text-right py-1">Var%</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.by_branch || []).map((r) => (
                  <tr key={r.branch_id} className="border-t border-[#EAE5D9]">
                    <td className="py-1.5">{r.branch_name || r.branch_id.slice(0, 8)}</td>
                    <td className="py-1.5 text-right">{r.batches}</td>
                    <td className="py-1.5 text-right font-mono">{r.output_qty}</td>
                    <td className="py-1.5 text-right font-mono">{inr(r.standard_rm_cost)}</td>
                    <td className="py-1.5 text-right font-mono">{inr(r.wastage_cost)}</td>
                    <td className="py-1.5 text-right"><Badge className={`border-0 ${varianceTone(r.rm_variance_pct)}`}>{r.rm_variance_pct}%</Badge></td>
                  </tr>
                ))}
                {(!summary || summary.by_branch?.length === 0) && <tr><td colSpan={6} className="text-center py-6 text-[#8B7355] text-xs">No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#EAE5D9] rounded-md p-4">
          <div className="uc-label mb-3"><AlertTriangle className="w-3 h-3 inline mr-1" /> Wastage by Reason</div>
          {(summary?.by_reason || []).length === 0 ? (
            <div className="text-center py-8 text-xs text-[#8B7355]">No wastage entries in range.</div>
          ) : (
            <DeferredChart height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.by_reason} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" fontSize={11} />
                  <YAxis dataKey="reason" type="category" width={110} fontSize={11} />
                  <Tooltip formatter={(v, k) => k === "cost" ? inr(v) : v} />
                  <Bar dataKey="cost" fill="#C05621" name="Wastage ₹" />
                </BarChart>
              </ResponsiveContainer>
            </DeferredChart>
          )}
        </div>
      </section>

      {/* Weekly trend + top SKUs */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-md p-4">
          <div className="uc-label mb-3"><BarChart3 className="w-3 h-3 inline mr-1" /> Weekly RM Variance %</div>
          {(summary?.weekly || []).length === 0 ? (
            <div className="text-center py-8 text-xs text-[#8B7355]">Not enough data.</div>
          ) : (
            <DeferredChart height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.weekly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" />
                  <XAxis dataKey="week" fontSize={11} />
                  <YAxis fontSize={11} unit="%" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="rm_variance_pct" stroke="#C05621" name="RM Variance %" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </DeferredChart>
          )}
        </div>

        <div className="bg-white border border-[#EAE5D9] rounded-md p-4">
          <div className="uc-label mb-3">Top 10 SKUs by Wastage ₹</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="yield-top-skus">
              <thead className="text-[#8B7355] text-[11px] uppercase">
                <tr>
                  <th className="text-left py-1">SKU</th>
                  <th className="text-right py-1">Output</th>
                  <th className="text-right py-1">Std ₹</th>
                  <th className="text-right py-1">Wastage ₹</th>
                  <th className="text-right py-1">Var%</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.top_skus || []).map((r) => (
                  <tr key={r.product_id} className="border-t border-[#EAE5D9]">
                    <td className="py-1.5"><div className="font-medium text-xs">{r.product_name || "—"}</div><div className="text-[10px] text-[#8B7355]">{r.product_sku}</div></td>
                    <td className="py-1.5 text-right font-mono">{r.output_qty}</td>
                    <td className="py-1.5 text-right font-mono">{inr(r.standard_rm_cost)}</td>
                    <td className="py-1.5 text-right font-mono">{inr(r.wastage_cost)}</td>
                    <td className="py-1.5 text-right"><Badge className={`border-0 ${varianceTone(r.rm_variance_pct)}`}>{r.rm_variance_pct}%</Badge></td>
                  </tr>
                ))}
                {(!summary || summary.top_skus?.length === 0) && <tr><td colSpan={5} className="text-center py-6 text-[#8B7355] text-xs">No SKUs produced in range.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Batches list */}
      <section className="bg-white border border-[#EAE5D9] rounded-md p-4">
        <div className="uc-label mb-3">Recent Batches — click a row for ingredient variance detail</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="yield-batches">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Batch #</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-right px-3 py-2">Target</th>
                <th className="text-right px-3 py-2">Actual</th>
                <th className="text-right px-3 py-2">Yield%</th>
                <th className="text-right px-3 py-2">Std ₹</th>
                <th className="text-right px-3 py-2">Wastage ₹</th>
                <th className="text-right px-3 py-2">Var%</th>
                <th className="text-center px-3 py-2">Entries</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7] cursor-pointer" onClick={() => openBatch(b.id)} data-testid={`yield-batch-row-${b.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{b.batch_no}</td>
                  <td className="px-3 py-2"><div className="font-medium">{b.product_name}</div><div className="text-[10px] text-[#8B7355]">{b.product_sku}</div></td>
                  <td className="px-3 py-2 text-right font-mono">{b.target_output}</td>
                  <td className="px-3 py-2 text-right font-mono">{b.actual_output}</td>
                  <td className="px-3 py-2 text-right"><Badge className={`border-0 ${yieldTone(b.yield_pct)}`}>{b.yield_pct}%</Badge></td>
                  <td className="px-3 py-2 text-right font-mono">{inr(b.standard_rm_cost)}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(b.wastage_cost)}</td>
                  <td className="px-3 py-2 text-right"><Badge className={`border-0 ${varianceTone(b.rm_variance_pct)}`}>{b.rm_variance_pct}%</Badge></td>
                  <td className="px-3 py-2 text-center text-xs">{b.wastage_entries}</td>
                </tr>
              ))}
              {batches.length === 0 && (<tr><td colSpan={9} className="text-center py-10 text-[#8B7355]">No production batches in this range.</td></tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Batch detail drawer */}
      <Dialog open={!!drawer} onOpenChange={(v) => !v && setDrawer(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Batch {drawer?.batch?.batch_no} — {drawer?.batch?.product_name}</DialogTitle>
          </DialogHeader>
          {drawer && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-4 gap-3">
                <KpiTile label="Target" value={drawer.output.target} icon={Package} tone="amber" testid="drawer-target" />
                <KpiTile label="Actual" value={drawer.output.actual} icon={Package} tone="emerald" testid="drawer-actual" />
                <KpiTile label="Yield%" value={`${drawer.output.yield_pct}%`} icon={Percent} isText tone={drawer.output.yield_pct >= 95 ? "emerald" : drawer.output.yield_pct >= 80 ? "amber" : "red"} testid="drawer-yield" />
                <KpiTile label="Actual RM ₹" value={inr(drawer.totals.actual_cost)} isText icon={DollarSign} tone="neutral" testid="drawer-actual-cost" />
              </div>

              {/* QR code traceability label */}
              <BatchQrPanel batchId={drawer.batch.id} batchNo={drawer.batch.batch_no} productName={drawer.batch.product_name} />

              <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Ingredient</th>
                      <th className="text-right px-3 py-2">Std</th>
                      <th className="text-right px-3 py-2">Wastage</th>
                      <th className="text-right px-3 py-2">Actual</th>
                      <th className="text-right px-3 py-2">Var%</th>
                      <th className="text-right px-3 py-2">Actual ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawer.ingredients.map((i) => (
                      <tr key={i.ingredient_id} className="border-t border-[#EAE5D9]">
                        <td className="px-3 py-2">{i.ingredient_name} <span className="text-[10px] text-[#8B7355]">({i.unit})</span></td>
                        <td className="px-3 py-2 text-right font-mono">{i.standard_qty}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">{i.wastage_qty > 0 ? `+${i.wastage_qty}` : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{i.actual_qty}</td>
                        <td className="px-3 py-2 text-right"><Badge className={`border-0 ${varianceTone(i.variance_pct)}`}>{i.variance_pct}%</Badge></td>
                        <td className="px-3 py-2 text-right font-mono">{inr(i.actual_cost)}</td>
                      </tr>
                    ))}
                    {drawer.ingredients.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-[#8B7355] text-xs">This batch had no recipe consumption on record.</td></tr>}
                  </tbody>
                </table>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="uc-label !text-[#5C4033]">Wastage Log ({drawer.wastage_entries.length})</div>
                  <Button size="sm" onClick={() => { setWastageForm({}); setWastageOpen(true); }} className="bg-red-600 text-white" data-testid="drawer-add-wastage">
                    <Plus className="w-3 h-3 mr-1" /> Log Wastage
                  </Button>
                </div>
                {drawer.wastage_entries.length === 0 ? (
                  <div className="text-center py-4 text-xs text-[#8B7355]">No wastage entries yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {drawer.wastage_entries.map((w) => (
                      <div key={w.id} className="flex items-center gap-3 border border-[#EAE5D9] rounded p-2 text-sm" data-testid={`drawer-wastage-${w.id}`}>
                        <Badge className="border-0 bg-red-50 text-red-700 text-[10px]">{w.reason}</Badge>
                        <div className="flex-1">
                          <div className="font-medium">{w.ingredient_name} — {w.quantity} {w.unit}</div>
                          {w.notes && <div className="text-[10px] text-[#8B7355]">{w.notes}</div>}
                        </div>
                        <div className="font-mono text-xs">{inr(w.cost)}</div>
                        <button className="text-red-500 hover:text-red-700" onClick={() => delWastage(w.id)} data-testid={`drawer-wastage-del-${w.id}`}><Trash className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrawer(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wastage form */}
      <Dialog open={wastageOpen} onOpenChange={setWastageOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-[Outfit]">Log Wastage — Batch {drawer?.batch?.batch_no}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label>Ingredient*</Label>
              <Select value={wastageForm.ingredient_id || ""} onValueChange={(v) => setWastageForm({ ...wastageForm, ingredient_id: v })}>
                <SelectTrigger data-testid="wastage-ingredient"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto">
                  {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} · {inr(i.cost_per_unit || 0)}/{i.unit}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Quantity wasted*</Label>
              <Input type="number" step="0.01" min="0" value={wastageForm.quantity || ""} onChange={(e) => setWastageForm({ ...wastageForm, quantity: e.target.value })} data-testid="wastage-qty" />
            </div>
            <div><Label>Reason*</Label>
              <Select value={wastageForm.reason || ""} onValueChange={(v) => setWastageForm({ ...wastageForm, reason: v })}>
                <SelectTrigger data-testid="wastage-reason"><SelectValue placeholder="Pick reason" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {reasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label>
              <Textarea rows={2} value={wastageForm.notes || ""} onChange={(e) => setWastageForm({ ...wastageForm, notes: e.target.value })} />
            </div>
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded">
              Ingredient stock will be deducted immediately. You can remove the entry later to refund.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWastageOpen(false)}>Cancel</Button>
            <Button onClick={saveWastage} className="bg-red-600 text-white" data-testid="wastage-save">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone, testid, isText }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
    neutral: "bg-white border-[#EAE5D9] text-[#2C1810]",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-lg p-4 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`font-[Outfit] font-semibold mt-1 ${isText ? "text-2xl" : "text-3xl"}`}>{value ?? 0}</div>
    </div>
  );
}

// ==================== Batch QR + Print Label ====================
function BatchQrPanel({ batchId, batchNo, productName }) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const url = `${base}/qr/batch/${encodeURIComponent(batchId)}`;

  const printLabel = () => {
    const svg = document.querySelector(`[data-testid="batch-qr-svg"] svg`);
    if (!svg) return;
    const svgHtml = svg.outerHTML;
    const w = window.open("", "_blank", "width=480,height=640");
    if (!w) return;
    w.document.write(`
      <html><head><title>Batch label — ${batchNo}</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 20px; text-align: center; }
        .label { display: inline-block; border: 2px solid #C05621; border-radius: 8px; padding: 16px; width: 340px; }
        .label h1 { font-size: 18px; margin: 4px 0; color: #5C4033; }
        .label .sub { font-size: 11px; color: #8B7355; margin-bottom: 8px; }
        .label .batch { font-family: monospace; font-size: 14px; letter-spacing: 1px; }
        .label .qr { margin: 12px auto; }
        .label .qr svg { width: 220px; height: 220px; }
        .label .foot { font-size: 10px; color: #8B7355; margin-top: 6px; }
        @media print { .label { border-color: #000; } }
      </style></head>
      <body onload="setTimeout(function(){ window.print(); window.close(); }, 250)">
        <div class="label">
          <h1>${productName || "Bakemill Foods"}</h1>
          <div class="sub">Scan to verify authenticity</div>
          <div class="qr">${svgHtml}</div>
          <div class="batch">${batchNo || batchId}</div>
          <div class="foot">bakemill.com</div>
        </div>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="border border-[#EAE5D9] rounded-md p-3 bg-[#FEF9EA] flex items-center gap-3" data-testid="batch-qr-panel">
      <div className="bg-white p-2 rounded border border-[#EAE5D9]" data-testid="batch-qr-svg">
        <QRCodeSVG value={url} size={120} bgColor="#FFFFFF" fgColor="#5C4033" level="M" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="uc-label !text-[#C05621]">QR Traceability Label</div>
        <div className="font-[Outfit] font-semibold text-sm">Scan for full batch trace</div>
        <div className="text-[11px] text-[#8B7355] truncate mt-1">
          Points to <code className="bg-white/70 px-1 rounded">{url}</code>
        </div>
        <button
          onClick={printLabel}
          data-testid="batch-print-label-btn"
          className="mt-2 h-8 px-3 bg-[#C05621] text-white text-xs rounded hover:bg-[#a04519]"
        >
          Print label
        </button>
      </div>
    </div>
  );
}

